import axios from "axios";

/**
 * Pick the API base URL.
 *
 * In dev / preview environments we ship a fixed REACT_APP_BACKEND_URL so the
 * Webpack dev server can proxy through. In production (custom domain like
 * pk2scarlett.com) the backend lives behind the same hostname under /api,
 * so we use the runtime origin to avoid CORS / cross-cookie issues.
 *
 * Heuristic:
 *   - If the page is served from one of the Emergent preview / localhost hosts,
 *     trust the build-time env var.
 *   - Otherwise (custom domain) → use the runtime origin.
 */
const BUILD_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function pickBackendUrl() {
  if (typeof window === "undefined") return BUILD_BACKEND_URL;
  const host = window.location.hostname || "";
  const isPreviewHost =
    host === "localhost" ||
    host.endsWith(".emergentagent.com") ||
    host.endsWith(".preview.emergentagent.com") ||
    host.endsWith(".vercel.app");
  if (isPreviewHost && BUILD_BACKEND_URL) return BUILD_BACKEND_URL;
  // Custom production domain → same-origin
  return `${window.location.protocol}//${window.location.host}`;
}

export const BACKEND_URL = pickBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auto-refresh access token on 401 so sessions only end when the user logs out.
let _refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const status = err.response?.status;
    const url = cfg.url || "";
    // Don't retry on auth routes or if we already retried
    const isAuthRoute =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");
    if (status === 401 && !cfg._retried && !isAuthRoute) {
      cfg._retried = true;
      try {
        _refreshing = _refreshing || api.post("/auth/refresh");
        await _refreshing;
        _refreshing = null;
        return api.request(cfg);
      } catch (e) {
        _refreshing = null;
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
